import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute — redirects unauthenticated users to the home page.
 * Students with restricted access are sent to /pending-approval with restricted state.
 */
const ProtectedRoute = ({ children }) => {
  const { user, userProfile, loading } = useAuth();
  const location = useLocation();

  // While auth state is being determined, show nothing (avoids flash)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-violet-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  // Not logged in → send to home page
  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Student whose access has been restricted by super admin
  if (
    userProfile?.role === 'student' &&
    userProfile?.student_profiles?.[0]?.is_restricted === true &&
    location?.pathname !== '/pending-approval'
  ) {
    return <Navigate to="/pending-approval" state={{ restricted: true }} replace />;
  }

  // Logged-in student whose account is still pending → send to pending-approval
  // (skip this check if they are already on that page to avoid redirect loop)
  if (
    userProfile?.role === 'student' &&
    userProfile?.student_profiles?.[0]?.approval_status === 'pending' &&
    location?.pathname !== '/pending-approval'
  ) {
    return <Navigate to="/pending-approval" replace />;
  }

  return children;
};

export default ProtectedRoute;
